// Which part of the site a pathname belongs to, for scoping the maintenance banner. Derived from
// the URL rather than from router matches so the same function serves SSR, the client and tests
// without a router context. Mirrors the route ids in packages/router/src/routeTree.gen.ts.
export type RouteArea = 'live' | 'on-demand' | 'general';

// Second path segment under /$webinarId. w/b/s/m/a are the link-code entry routes for viewers,
// broadcasters, slide controllers, moderators and authorised anonymous viewers of a live webinar.
const LIVE_SEGMENTS = new Set(['watch', 'broadcast', 'slide-controller', 'slide-viewer', 'w', 'b', 'a', 's', 'm']);
const ON_DEMAND_SEGMENTS = new Set(['on-demand', 'o']);

// Segments built on the player organisms (rendered inside ViewerLiveWebinarLayout, shared with the
// embed widget). Deliberately excludes the staff segments (broadcast, b, s, m, slide-controller):
// those pages have no player organism and no banner of their own.
const PLAYER_SEGMENTS = new Set(['watch', 'w', 'a', 'o', 'on-demand', 'slide-viewer']);

// Lowercased because TanStack Router matches case-insensitively: /$webinarId/Watch is the watch
// page, so it must read as the live area here too.
const pathSegments = (pathname: string): string[] =>
    pathname
        .toLowerCase()
        .split('/')
        .filter((part) => part.length > 0);

export const getRouteArea = (pathname: string): RouteArea => {
    const parts = pathSegments(pathname);
    if (parts.length === 0) {
        return 'general';
    }
    // Top-level entry routes: /w/$linkCode and the /on-demand catalogue.
    if (parts[0] === 'w') {
        return 'live';
    }
    if (parts[0] === 'on-demand') {
        return 'on-demand';
    }
    if (parts.length >= 2) {
        if (LIVE_SEGMENTS.has(parts[1])) {
            return 'live';
        }
        if (ON_DEMAND_SEGMENTS.has(parts[1])) {
            return 'on-demand';
        }
    }
    return 'general';
};

// True only for the second-segment pages built on the player organisms (watch, w, a, o, on-demand,
// slide-viewer), which render the maintenance banner themselves, inside ViewerLiveWebinarLayout
// (shared with the embed widget) — the root mount must stay out of their way. False for the staff
// pages (broadcast, moderator, speaker, slide controller), the top-level /on-demand catalogue and
// the top-level /w/$linkCode landing page (WatchLinkPage, which has no player organism either):
// those get the banner from the root mount instead.
export const isPlayerPage = (pathname: string): boolean => {
    const parts = pathSegments(pathname);
    if (parts.length === 0) {
        return false;
    }
    if (parts[0] === 'on-demand') {
        return false;
    }
    return parts.length >= 2 && PLAYER_SEGMENTS.has(parts[1]);
};
